import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Wordmark from "@/components/Wordmark";
import { ArrowLeft, Check, Loader2 } from "lucide-react";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    api
      .get(`/auth/password-reset/${token}`)
      .then(({ data }) => {
        if (!mounted) return;
        setValid(!!data.valid);
        setReason(data.reason || "");
      })
      .catch(() => {
        if (!mounted) return;
        setValid(false);
        setReason("network");
      })
      .finally(() => mounted && setChecking(false));
    return () => {
      mounted = false;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      await api.post(`/auth/password-reset/${token}`, { password });
      setDone(true);
      setTimeout(() => navigate("/login?reset=1"), 1500);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#FAF9F7] flex flex-col font-sans"
      data-testid="reset-password-page"
    >
      <header className="px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/brand/onebrain-mark-192.png"
            alt=""
            className="w-7 h-7 rounded-md"
          />
          <Wordmark size="md" testid="reset-header-wordmark" />
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-[#7A7571] hover:text-[#2A2D34] mb-6"
            data-testid="reset-back-to-login"
          >
            <ArrowLeft size={14} />
            Back to sign in
          </Link>
          {checking ? (
            <p className="font-sans text-sm text-[#7A7571] flex items-center gap-2">
              <Loader2 className="animate-spin" size={14} />
              Verifying link…
            </p>
          ) : !valid ? (
            <div
              className="rounded-xl border border-[#D96C4A]/30 bg-[#FAE7DC] p-5"
              data-testid="reset-invalid"
            >
              <h2 className="font-serif text-2xl text-[#2A2D34]">
                That link no longer works
              </h2>
              <p className="font-sans text-sm text-[#3F4148] mt-2 leading-relaxed">
                {reason === "expired"
                  ? "Reset links expire after one hour. Request a fresh one."
                  : "This link is invalid or has already been used."}
              </p>
              <Link
                to="/forgot-password"
                className="mt-4 inline-block font-sans text-sm font-medium text-[#1E7BFF] hover:text-[#155EC9]"
              >
                Request a new link →
              </Link>
            </div>
          ) : done ? (
            <div
              className="rounded-xl border border-[#1E7BFF]/30 bg-[#F4F8FF] p-5 flex gap-3 items-start"
              data-testid="reset-done"
            >
              <div className="w-9 h-9 rounded-full bg-[#1E7BFF] text-white flex items-center justify-center shrink-0">
                <Check size={16} strokeWidth={2.5} />
              </div>
              <div>
                <h2 className="font-serif text-xl text-[#2A2D34]">
                  Password updated
                </h2>
                <p className="font-sans text-sm text-[#3F4148] mt-1">
                  Redirecting you to sign in…
                </p>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-3xl sm:text-4xl text-[#2A2D34]">
                Set a new password
              </h1>
              <p className="font-sans text-sm text-[#3F4148] mt-3">
                At least 8 characters.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <label
                    htmlFor="reset-password-input"
                    className="block font-sans text-xs text-[#7A7571] mb-1.5"
                  >
                    New password
                  </label>
                  <input
                    id="reset-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    data-testid="reset-password-input"
                    className="w-full px-4 py-3 rounded-lg bg-white border border-[#DCD7CE] font-sans text-base text-[#2A2D34] focus:outline-none focus:ring-2 focus:ring-[#1E7BFF]/30"
                  />
                </div>
                <div>
                  <label
                    htmlFor="reset-password-confirm"
                    className="block font-sans text-xs text-[#7A7571] mb-1.5"
                  >
                    Confirm new password
                  </label>
                  <input
                    id="reset-password-confirm"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    minLength={8}
                    data-testid="reset-password-confirm"
                    className="w-full px-4 py-3 rounded-lg bg-white border border-[#DCD7CE] font-sans text-base text-[#2A2D34] focus:outline-none focus:ring-2 focus:ring-[#1E7BFF]/30"
                  />
                </div>
                {err ? (
                  <p
                    className="font-sans text-sm text-[#8C3A1F]"
                    data-testid="reset-error"
                  >
                    {err}
                  </p>
                ) : null}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="reset-submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#1E7BFF] hover:bg-[#155EC9] disabled:opacity-50 text-white font-sans font-medium text-sm px-5 py-3 rounded-lg transition-colors"
                >
                  {submitting ? (
                    <Loader2 className="animate-spin" size={15} />
                  ) : (
                    "Update password"
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
